/**
 * API Client for Gesty Admin
 * Centralized API configuration with configurable base URL
 */

import axios from 'axios';
import { auth } from '../firebase';

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0']);

const isLocalLikeUrl = (value) => {
  if (!value || typeof value !== 'string') return false;
  try {
    const parsed = new URL(value);
    return LOCAL_HOSTNAMES.has(parsed.hostname);
  } catch (error) {
    return false;
  }
};

const resolveApiBaseUrl = () => {
  const envBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const defaultProdBaseUrl = 'https://api.servicegest.ro';

  if (!envBaseUrl) {
    return defaultProdBaseUrl;
  }

  // Safety guard: if a production frontend is built with localhost API env by mistake,
  // force the backend host to api.servicegest.ro.
  const runningOnLocalFrontend =
    typeof window !== 'undefined' && window.location && LOCAL_HOSTNAMES.has(window.location.hostname);

  if (!runningOnLocalFrontend && isLocalLikeUrl(envBaseUrl)) {
    return defaultProdBaseUrl;
  }

  return envBaseUrl;
};

// Base URL configuration - can be changed for different environments
const API_CONFIG = {
  baseUrl: resolveApiBaseUrl(),
};

/**
 * Get the configured base URL
 */
export const getBaseUrl = () => API_CONFIG.baseUrl;

/**
 * Set a new base URL (useful for switching environments)
 */
export const setBaseUrl = (url) => {
  API_CONFIG.baseUrl = url;
};

/**
 * Create an axios instance with auth interceptor
 */
const createApiClient = () => {
  const client = axios.create({
    baseURL: API_CONFIG.baseUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor to add auth token
  client.interceptors.request.use(
    async (config) => {
      try {
        const token = await auth.currentUser?.getIdToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting auth token:', error);
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor for error handling
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        console.error('Unauthorized - token may be expired');
      }
      return Promise.reject(error);
    }
  );

  return client;
};

// Export a function that creates fresh client (to get updated baseUrl)
export const getApiClient = () => createApiClient();

// Default export for convenience
const apiClient = {
  getBaseUrl,
  setBaseUrl,
  getApiClient,
};

export default apiClient;
